import { IconArrowDown, IconDownload, IconLink } from '@viabizzuno/icons';
import { getClassNames, IMenuHref } from '@websolutespa/bom-core';
import { Link, Text } from '@websolutespa/bom-mixer-ui';
import React, { useId, useMemo } from 'react';
import { useDownloadModal } from '../../hooks';
import { CtaContainer } from './style';

export type IMenuLocked = IMenuHref & {
  lockedAttachmentId?: string;
};

export type CtaProps = {
  id?: string;
  lockedAttachmentId?: string;
  as?: React.ElementType;
  target?: string;
  variant: 'primary' | 'secondary' | 'link' | 'link-reverse' | 'ghost';
  label: string;
  href?: string;
  contrast?: boolean;
  icon?: React.ComponentType;
  iconPosition?: 'left' | 'right';
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  icononly?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const CtaBase: React.FC<CtaProps> = (props: CtaProps) => {
  const {
    onClick,
    contrast,
    variant,
    label,
    icon,
    iconPosition,
    lockedAttachmentId,
    href,
    target,
    as,
    icononly,
    ...rest
  } = props;

  const { openDownloadModal } = useDownloadModal();

  const onLockedFileClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (!lockedAttachmentId) return;
    openDownloadModal({ fileId: lockedAttachmentId });
  };

  const isExternal = useMemo(() => {
    return !lockedAttachmentId && target === '_blank';
  }, [lockedAttachmentId, target]);

  const isAttachment = useMemo(() => {
    return (
      lockedAttachmentId !== undefined || (href && href.includes('attachment'))
    );
  }, [lockedAttachmentId]);

  const isAnchor = useMemo(() => {
    return !lockedAttachmentId && href && href.startsWith('#') && !isExternal;
  }, [lockedAttachmentId, href]);

  return (
    <CtaContainer
      as={as}
      href={href}
      target={target}
      onClick={lockedAttachmentId ? onLockedFileClick : onClick}
      className={getClassNames('v-cta', {
        [`-${variant}`]: true,
        ['-contrast']: contrast,
        [`-icon-${iconPosition}`]: iconPosition,
        ['-icononly']: icononly,
      })}
      {...rest}>
      {!icononly && (
        <Text as="div" variant="cta2">
          <span suppressHydrationWarning>{label}</span>
        </Text>
      )}
      {icon && <i aria-hidden="true">{React.createElement(icon)}</i>}
      {!icon && isAttachment && (
        <i aria-hidden="true">
          <IconDownload />
        </i>
      )}
      {!icon && isAnchor && (
        <i aria-hidden="true">
          <IconArrowDown />
        </i>
      )}
      {!icon && isExternal && (
        <i aria-hidden="true">
          <IconLink />
        </i>
      )}
    </CtaContainer>
  );
};

export const Cta: React.FC<CtaProps> = (props: CtaProps) => {
  const { href, target } = props;
  const id = useId();
  const openInNewTab = target === '_blank';

  return href && !openInNewTab ? (
    <Link href={href} target={target}>
      <CtaBase as="a" {...props} id={id} />
    </Link>
  ) : href ? (
    //wordkaround for relative URL target blank not working
    <CtaBase
      as="a"
      {...props}
      id={id}
      rel={props.rel ?? (openInNewTab ? 'noopener noreferrer' : undefined)}
    />
  ) : (
    <CtaBase as="button" {...props} id={id} />
  );
};
